import React, { useState, useEffect, useCallback } from 'react'
import { api, AdminContext, AdminSkill, ContextMember } from '@services/api'
import { ChevronDown, ChevronUp, Edit2, Check, X, UserPlus, Trash2, RefreshCw, Settings, Layers } from 'lucide-react'
import { toast } from 'react-toastify'

// ─── UUID validation ────────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// ─── ContextCard ────────────────────────────────────────────────────────────

interface ContextCardProps {
  ctx: AdminContext
  skills: AdminSkill[]
  onContextUpdated: (updated: AdminContext) => void
}

const ContextCard: React.FC<ContextCardProps> = ({ ctx, skills, onContextUpdated }) => {
  const [membersOpen, setMembersOpen] = useState(false)
  const [members, setMembers] = useState<ContextMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)

  const [editingEntraId, setEditingEntraId] = useState(false)
  const [entraIdValue, setEntraIdValue] = useState(ctx.entra_group_id ?? '')
  const [entraIdSaving, setEntraIdSaving] = useState(false)

  const [skillId, setSkillId] = useState<number | null>(ctx.preferred_skill_id ?? null)
  const [skillExclusive, setSkillExclusive] = useState<boolean>(ctx.skill_exclusive ?? false)
  const [skillDescription, setSkillDescription] = useState<string>(ctx.skill_context_description ?? '')
  const [skillSaving, setSkillSaving] = useState(false)

  const [addOidValue, setAddOidValue] = useState('')
  const [addLoading, setAddLoading] = useState(false)

  const loadMembers = useCallback(async () => {
    setMembersLoading(true)
    try {
      const data = await api.adminGetMembers(ctx.id)
      setMembers(data)
    } catch {
      toast.error(`Failed to load members for ${ctx.display_name}`)
    } finally {
      setMembersLoading(false)
    }
  }, [ctx.id, ctx.display_name])

  useEffect(() => {
    if (membersOpen) loadMembers()
  }, [membersOpen, loadMembers])

  const handleSaveEntraId = async () => {
    const trimmed = entraIdValue.trim()
    if (trimmed !== '' && !UUID_RE.test(trimmed)) {
      toast.error('Entra Group ID must be a valid UUID (e.g. 0b0c9c41-58d1-4928-b5d6-cb03415f90c1)')
      return
    }
    setEntraIdSaving(true)
    try {
      const updated = await api.adminUpdateEntraGroupId(ctx.id, trimmed === '' ? null : trimmed)
      onContextUpdated(updated)
      setEditingEntraId(false)
      toast.success(`Entra Group ID updated for ${ctx.display_name}`)
    } catch {
      toast.error('Failed to update Entra Group ID')
    } finally {
      setEntraIdSaving(false)
    }
  }

  const handleCancelEntraId = () => {
    setEntraIdValue(ctx.entra_group_id ?? '')
    setEditingEntraId(false)
  }

  const handleSaveSkill = async () => {
    setSkillSaving(true)
    try {
      const updated = await api.adminUpdateContextSkill(ctx.id, {
        preferred_skill_id: skillId,
        skill_exclusive: skillId === null ? false : skillExclusive,
        skill_context_description: skillDescription.trim() === '' ? null : skillDescription.trim(),
      })
      onContextUpdated(updated)
      toast.success(`Skill assignment updated for ${ctx.display_name}`)
    } catch {
      toast.error('Failed to update skill assignment')
    } finally {
      setSkillSaving(false)
    }
  }

  const handleAddMember = async () => {
    const oid = addOidValue.trim()
    if (!oid) return
    setAddLoading(true)
    try {
      await api.adminAddMember(ctx.id, oid)
      setAddOidValue('')
      await loadMembers()
      toast.success('Member added')
    } catch {
      toast.error('Failed to add member — check the OID is valid and the user exists in the DB')
    } finally {
      setAddLoading(false)
    }
  }

  const handleRemoveMember = async (member: ContextMember) => {
    try {
      await api.adminRemoveMember(ctx.id, member.oid)
      setMembers((prev) => prev.filter((m) => m.oid !== member.oid))
      toast.success(`Removed ${member.display_name}`)
    } catch {
      toast.error('Failed to remove member')
    }
  }

  return (
    <div className="card overflow-hidden p-0">
      {/* Card header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-white/5">
        <div className="flex items-center space-x-3">
          <Layers className="h-5 w-5 text-indigo-500" />
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100">{ctx.display_name}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{ctx.name}</p>
          </div>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            ctx.is_active
              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
          }`}
        >
          {ctx.is_active ? 'active' : 'inactive'}
        </span>
      </div>

      <div className="space-y-4 p-5">
        {/* Description */}
        {ctx.description && <p className="text-sm text-gray-500 dark:text-gray-400">{ctx.description}</p>}

        {/* Entra Group ID */}
        <div>
          <p className="mb-1.5 text-xs font-semibold tracking-wide text-gray-400 uppercase dark:text-gray-500">
            Entra Group ID
          </p>
          {editingEntraId ? (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={entraIdValue}
                onChange={(e) => setEntraIdValue(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 font-mono text-sm focus:border-indigo-500 focus:outline-none dark:border-gray-600"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveEntraId()
                  if (e.key === 'Escape') handleCancelEntraId()
                }}
                autoFocus
              />
              <button
                onClick={handleSaveEntraId}
                disabled={entraIdSaving}
                className="rounded-lg bg-indigo-600 p-1.5 text-white hover:bg-indigo-700 disabled:opacity-50"
                title="Save"
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                onClick={handleCancelEntraId}
                className="rounded-lg border border-gray-300 p-1.5 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-900"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <span
                className={`flex-1 font-mono text-sm ${ctx.entra_group_id ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}
              >
                {ctx.entra_group_id ?? 'Not configured'}
              </span>
              <button
                onClick={() => {
                  setEntraIdValue(ctx.entra_group_id ?? '')
                  setEditingEntraId(true)
                }}
                className="rounded-lg border border-gray-200 p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 dark:border-gray-700 dark:text-gray-500 dark:hover:bg-gray-900 dark:hover:text-gray-400"
                title="Edit"
              >
                <Edit2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* AI Skill Assignment */}
        <div>
          <label
            htmlFor={`skill-select-${ctx.id}`}
            className="mb-1.5 block text-xs font-semibold tracking-wide text-gray-400 uppercase"
          >
            AI Assistant Skill
          </label>
          <select
            id={`skill-select-${ctx.id}`}
            value={skillId ?? ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : Number(e.target.value)
              setSkillId(value)
              if (value === null) {
                setSkillExclusive(false)
                setSkillDescription('')
              }
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
          >
            <option value="">None — full skill catalog available</option>
            {skills.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          {skillId !== null && (
            <>
              <label className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
                <input type="checkbox" checked={skillExclusive} onChange={(e) => setSkillExclusive(e.target.checked)} />
                <span>Exclusive — only this skill is offered to the model</span>
              </label>

              <textarea
                value={skillDescription}
                onChange={(e) => setSkillDescription(e.target.value)}
                placeholder="Describe when the model should apply this skill for this context..."
                aria-label="Context guidance for this skill"
                rows={3}
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </>
          )}

          <button
            onClick={handleSaveSkill}
            disabled={skillSaving}
            className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {skillSaving ? 'Saving...' : 'Save Skill Assignment'}
          </button>
        </div>

        {/* Members */}
        <div>
          <button
            onClick={() => setMembersOpen((o) => !o)}
            className="flex w-full items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-900"
          >
            <span className="font-medium">Manage Members</span>
            <div className="flex items-center space-x-1.5">
              {!membersOpen && members.length > 0 && (
                <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                  {members.length}
                </span>
              )}
              {membersOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              )}
            </div>
          </button>

          {membersOpen && (
            <div className="mt-2 space-y-2">
              {/* Add member */}
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={addOidValue}
                  onChange={(e) => setAddOidValue(e.target.value)}
                  placeholder="User OID (Entra Object ID)"
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 font-mono text-xs focus:border-indigo-500 focus:outline-none dark:border-gray-600"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddMember()
                  }}
                />
                <button
                  onClick={handleAddMember}
                  disabled={!addOidValue.trim() || addLoading}
                  className="flex items-center space-x-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  <span>Add</span>
                </button>
                <button
                  onClick={loadMembers}
                  className="rounded-lg border border-gray-200 p-1.5 text-gray-400 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-500 dark:hover:bg-gray-900"
                  title="Refresh"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Member list */}
              {membersLoading ? (
                <p className="py-2 text-center text-xs text-gray-400 dark:text-gray-500">Loading...</p>
              ) : members.length === 0 ? (
                <p className="py-2 text-center text-xs text-gray-400 dark:text-gray-500">No members yet.</p>
              ) : (
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
                  {members.map((m) => (
                    <div key={m.oid} className="flex items-center justify-between px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                          {m.display_name || m.email}
                        </p>
                        <p className="truncate font-mono text-xs text-gray-400 dark:text-gray-500">{m.oid}</p>
                      </div>
                      <div className="ml-3 flex items-center space-x-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            m.source === 'entra_sync'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          }`}
                        >
                          {m.source === 'entra_sync' ? 'entra' : 'manual'}
                        </span>
                        {m.source === 'manual' && (
                          <button
                            onClick={() => handleRemoveMember(m)}
                            className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                            title="Remove membership"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── ContextConfigurator ─────────────────────────────────────────────────────────────

const ContextConfigurator: React.FC = () => {
  const [contexts, setContexts] = useState<AdminContext[]>([])
  const [skills, setSkills] = useState<AdminSkill[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [contextsData, skillsData] = await Promise.all([api.adminGetContexts(), api.adminGetSkills()])
        setContexts(contextsData)
        setSkills(skillsData)
      } catch {
        toast.error('Failed to load contexts')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleContextUpdated = (updated: AdminContext) => {
    setContexts((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
  }

  if (loading) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-10 w-10 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">Loading contexts...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Settings className="h-7 w-7 text-indigo-500" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Context Configurator</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage contexts, Entra group IDs, and user memberships
            </p>
          </div>
        </div>
      </div>

      {/* Context cards */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
        {contexts.map((ctx) => (
          <ContextCard key={ctx.id} ctx={ctx} skills={skills} onContextUpdated={handleContextUpdated} />
        ))}
      </div>
    </div>
  )
}

export default ContextConfigurator
